import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { getShopInfoAction } from 'states/actions/shop';
import { SHOPINFO_SYNC_KEY } from 'utils/shopInfoCrossTabSync';

// The initial app bootstrap (App.js's getDashboardBootstrapAction) already
// covers a fresh shopInfo/permissions load — an embedded iframe's very first
// `focus`/`visibilitychange` event fires almost immediately as Shopify Admin
// finishes embedding the app, which used to re-trigger this hook's OLD,
// un-consolidated getShopInfoAction() RIGHT in the same critical window,
// firing 3 separate requests (shops/info, shops/storage, pricing/planUsage)
// that raced the new bootstrap call for the same session token instead of
// sharing it — measured on a real [fetchInstance][PERF] trace: this early
// burst finished in ~1s, then bootstrap paid its OWN ~830ms token cost
// instead of reusing the (by-then-resolved) shared one. Ignoring refetches
// within this window lets a genuine LATER tab-switch still resync normally.
const IGNORE_REFETCH_WITHIN_MS = 3000;

export function useShopInfoCrossTabSync() {
  const dispatch = useDispatch();

  useEffect(() => {
    const mountedAt = Date.now();
    const refetch = () => {
      if (Date.now() - mountedAt < IGNORE_REFETCH_WITHIN_MS) return;
      try {
        getShopInfoAction(dispatch)();
      } catch (e) {}
    };

    const onStorage = (e) => {
      if (e.key !== SHOPINFO_SYNC_KEY) return;
      refetch();
    };

    const onFocus = () => refetch();

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') refetch();
    };

    window.addEventListener('storage', onStorage);
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [dispatch]);
}